import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { BookingDetails } from "@repo/ui/types/booking";
import type { SsrAvailability } from "@repo/ui/types/ssr";

import { useAppSelector } from "../hooks";
import type { RootState } from "../store";

export interface BookingPassenger {
  passengerKey: string;
  passengerTypeCode: string;
  discountCode?: string;
  program?: { code: string; levelCode: string; number: string };
}

export interface BookingState {
  isCheckIn: boolean;
  isPostBooking: boolean;
  passengers: BookingPassenger[];
  isBookingInProgress: boolean;
  bookingDetails: BookingDetails | null;
  ssrAvailability: SsrAvailability | null;
}

const initialState: BookingState = {
  isCheckIn: false,
  isPostBooking: false,
  passengers: [],
  isBookingInProgress: false,
  bookingDetails: null,
  ssrAvailability: null,
};

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    setIsCheckIn: (state, action: PayloadAction<boolean>) => {
      state.isCheckIn = action.payload;
    },
    setIsPostBooking: (state, action: PayloadAction<boolean>) => {
      state.isPostBooking = action.payload;
    },
    setBookingPassengers: (state, action: PayloadAction<BookingPassenger[]>) => {
      state.passengers = action.payload;
    },
    setBookingInProgress: (state, action: PayloadAction<boolean>) => {
      state.isBookingInProgress = action.payload;
    },
    resetBooking: (state) => {
      state.passengers = [];
      // Note: isBookingInProgress is NOT reset here as it's controlled manually
      // based on the booking flow state (true during booking, false after payment)
      state.bookingDetails = null;
      state.ssrAvailability = null;
    },
    setBookingDetails: (state, action: PayloadAction<BookingDetails | null>) => {
      state.bookingDetails = action.payload;
    },
    resetBookingDetails: (state) => {
      state.bookingDetails = null;
    },
    setSsrAvailability: (state, action: PayloadAction<SsrAvailability>) => {
      state.ssrAvailability = action.payload;
    },
  },
});

// Selectors
export const selectIsCheckIn = (s: RootState) => s.booking.isCheckIn;
export const selectBookingDetails = (s: RootState) => s.booking.bookingDetails;
export const selectSsrAvailability = (s: RootState) => s.booking.ssrAvailability;
export const selectIsPostBooking = (s: RootState) => s.booking.isPostBooking;

// Hooks
export const useBookingState = () => useAppSelector((state) => state.booking);
export const useBookingPassengers = () => useAppSelector((state) => state.booking.passengers);
export const useBookingDetails = () => useAppSelector((state) => state.booking.bookingDetails);
export const useIsCheckin = () => useAppSelector((state) => state.booking.isCheckIn);
export const useIsPostBooking = () => useAppSelector((state) => state.booking.isPostBooking);

export const {
  setIsCheckIn,
  setIsPostBooking,
  setBookingPassengers,
  setBookingInProgress,
  resetBooking,
  setBookingDetails,
  resetBookingDetails,
  setSsrAvailability,
} = bookingSlice.actions;

export default bookingSlice.reducer;
